import { useCallback } from 'react';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { getProviderFromPaymentMethod } from '@/utils/paymentParams';
import {
  isStripeSuccessResponse,
  isStripeFailedResponse,
  isStripeRedirectResponse,
  isNuveiSuccessResponse,
  isBrainTreeSuccessResponse,
  isAdyenFinalizePaymentResponse,
  isAdyenRedirectResponse,
  isPaypalSuccessResponse,
  isNuveiFailedResponse,
  isNuveiRedirectResponse,
  isAdyenFailedResponse,
  isAdyenSuccessResponse,
  isPaypalFailedResponse,
  isBrainTreeFailedResponse,
  isBrainTreeRedirectResponse,
} from '@/types/guards';
import type { PaymentKeys, PaymentMethod } from '@/types/checkout';
import { AppchargeErrorType, type BasePaymentResponse } from '@/types/payment';
import type { RedirectPaymentContext, ThreeDSSecurityState } from '@/types/context';
import { getRedirectPaymentData } from '@/utils/redirectPayment';
import { getCurrentDomain, LocationRoutes } from '@/utils/location';
import { isMobileDevice } from '@/utils/device';
import { adyenService } from '@/services/adyenService';
import { adyenExpressService } from '@/services/adyenExpressService';
import { adyenApplePayService } from '@/services/adyenApplePayService';
import { logInfo } from '@/utils/logger';
import { InfoLogs } from '@/constants/logs';

interface UsePaymentResultsParams {
  paymentMethods: PaymentMethod[];
  onSuccess: (result: BasePaymentResponse, provider: ProviderEnum) => void;
  onError: (appChargeErrorType?: AppchargeErrorType, provider?: ProviderEnum) => void;
  onRedirect: (redirectData: RedirectPaymentContext) => void;
  on3DSRequired: (state: ThreeDSSecurityState) => void;
  actions: {
    stopPaymentProcess: () => void;
  };
}

interface UsePaymentResultsReturn {
  handlePaymentResults: (
    results: unknown,
    selectedPaymentMethod: PaymentMethodEnum,
    paymentKeys: PaymentKeys
  ) => Promise<unknown>;
}

export const usePaymentResults = ({
  paymentMethods,
  onSuccess,
  onError,
  onRedirect,
  on3DSRequired,
  actions,
}: UsePaymentResultsParams): UsePaymentResultsReturn => {
  const handleStripeResponse = useCallback(
    async (
      results: unknown,
      provider: ProviderEnum,
      selectedPaymentMethod: PaymentMethodEnum,
      stripePaymentKey: string
    ) => {
      if (isStripeSuccessResponse(results, provider)) {
        onSuccess(results as BasePaymentResponse, provider);
        return;
      }

      if (isStripeFailedResponse(results, provider)) {
        actions.stopPaymentProcess();
        onError(results?.appChargeErrorType, provider);
        return;
      }

      if (isStripeRedirectResponse(results, provider)) {
        const redirectData = await getRedirectPaymentData(results, ProviderEnum.STRIPE);
        let redirectUrl = redirectData.redirectUrl;
        if (selectedPaymentMethod === PaymentMethodEnum.CASH_APP && isMobileDevice()) {
          redirectUrl = `${getCurrentDomain()}/${LocationRoutes.PAYMENT_HANDLER}/${redirectData.clientSecret}/${stripePaymentKey}`;
        }

        if (selectedPaymentMethod === PaymentMethodEnum.CREDIT_CARD) {
          on3DSRequired({
            show3DSForm: true,
            provider: ProviderEnum.STRIPE,
            params: results,
            handlePaymentSuccess: () => onSuccess(results as BasePaymentResponse, provider),
            handlePaymentError: () => {
              actions.stopPaymentProcess();
              onError(results?.appChargeErrorType, provider);
            },
          });
          return;
        }
        onRedirect({
          ...redirectData,
          redirectUrl,
          showRedirect: true,
          paymentMethod: selectedPaymentMethod,
          orderId: results.orderId,
          handlePaymentSuccess: () => onSuccess(results as BasePaymentResponse, provider),
        });
      }
    },
    [onSuccess, onError, onRedirect, actions]
  );

  const handleNuveiResponse = useCallback(
    async (results: unknown, provider: ProviderEnum) => {
      if (isNuveiSuccessResponse(results, provider)) {
        onSuccess(results as BasePaymentResponse, provider);
      }
      if (isNuveiFailedResponse(results, provider)) {
        actions.stopPaymentProcess();
        onError(results?.appChargeErrorType, provider);
      }
      if (isNuveiRedirectResponse(results, provider)) {
        const redirectData = await getRedirectPaymentData(results, ProviderEnum.NUVEI);
        onRedirect({
          ...redirectData,
          clientSecret: results.sessionToken,
          provider: ProviderEnum.NUVEI,
          handlePaymentSuccess: () => onSuccess(results as BasePaymentResponse, provider),
        });
      }
    },
    [onSuccess, onError, onRedirect, actions]
  );

  const handleBraintreeResponse = useCallback(
    async (results: unknown, provider: ProviderEnum) => {
      if (isBrainTreeSuccessResponse(results, provider)) {
        onSuccess(results as BasePaymentResponse, provider);
      }
      if (isBrainTreeFailedResponse(results, provider)) {
        actions.stopPaymentProcess();
        onError(results?.appChargeErrorType, provider);
      }
      if (isBrainTreeRedirectResponse(results, provider)) {
        const redirectData = await getRedirectPaymentData(results, ProviderEnum.BRAINTREE);
        onRedirect({
          ...redirectData,
          provider: ProviderEnum.BRAINTREE,
          handlePaymentSuccess: () => onSuccess(results as BasePaymentResponse, provider),
        });
      }
    },
    [onSuccess, onError, onRedirect, actions]
  );

  const handleAdyenResponse = useCallback(
    async (results: unknown, provider: ProviderEnum, selectedPaymentMethod: PaymentMethodEnum) => {
      if (isAdyenFinalizePaymentResponse(results)) {
        if (isAdyenRedirectResponse(results)) {
          if (selectedPaymentMethod === PaymentMethodEnum.CREDIT_CARD) {
            on3DSRequired({
              show3DSForm: true,
              provider: ProviderEnum.ADYEN,
              params: results,
              handlePaymentSuccess: () => {
                onSuccess(results as BasePaymentResponse, provider);
              },
              handlePaymentError: () => {
                actions.stopPaymentProcess();
                onError(results?.appChargeErrorType, provider);
              },
            });
          } else {
            const redirectData = await getRedirectPaymentData(results, ProviderEnum.ADYEN);
            onRedirect({
              ...redirectData,
              provider: ProviderEnum.ADYEN,
              handlePaymentSuccess: () => onSuccess(results as BasePaymentResponse, provider),
            });
          }
        }
        if (isAdyenFailedResponse(results)) {
          actions.stopPaymentProcess();
          if (selectedPaymentMethod === PaymentMethodEnum.GOOGLE_PAY) {
            adyenExpressService.completeGooglePayPayment(false);
          } else if (selectedPaymentMethod === PaymentMethodEnum.APPLE_PAY) {
            adyenApplePayService.completeApplePayPayment(false);
          } else {
            adyenService.runPaymentFailed();
          }
          onError(results?.appChargeErrorType, provider);
        }
        if (isAdyenSuccessResponse(results)) {
          if (selectedPaymentMethod === PaymentMethodEnum.GOOGLE_PAY) {
            adyenExpressService.completeGooglePayPayment(true, results.adyen.resultCode);
          } else if (selectedPaymentMethod === PaymentMethodEnum.APPLE_PAY) {
            adyenApplePayService.completeApplePayPayment(true, results.adyen.resultCode);
          }
          onSuccess(results as BasePaymentResponse, provider);
        }
      }
    },
    [onSuccess, onError, on3DSRequired, onRedirect, actions]
  );

  const handlePaypalResponse = useCallback(
    async (results: unknown, provider: ProviderEnum) => {
      if (isPaypalSuccessResponse(results, provider)) {
        onSuccess(results as BasePaymentResponse, provider);
      }
      if (isPaypalFailedResponse(results, provider)) {
        actions.stopPaymentProcess();
        onError(results?.appChargeErrorType, provider);
      }
    },
    [onSuccess, onError, actions]
  );

  const handlePaymentResults = useCallback(
    async (results: unknown, selectedPaymentMethod: PaymentMethodEnum, paymentKeys: PaymentKeys): Promise<unknown> => {
      logInfo(InfoLogs.checkoutPaymentProcessed, {
        selectedPaymentMethod,
        results,
      });
      const provider = getProviderFromPaymentMethod(selectedPaymentMethod, paymentMethods);
      switch (provider) {
        case ProviderEnum.PAYPAL:
          await handlePaypalResponse(results, provider);
          break;
        case ProviderEnum.STRIPE:
          await handleStripeResponse(results, provider, selectedPaymentMethod, paymentKeys.stripe);
          break;

        case ProviderEnum.NUVEI:
          handleNuveiResponse(results, provider);
          break;

        case ProviderEnum.BRAINTREE:
          handleBraintreeResponse(results, provider);
          break;

        case ProviderEnum.ADYEN:
          handleAdyenResponse(results, provider, selectedPaymentMethod);
          break;

        default:
          break;
      }

      return results;
    },
    [
      paymentMethods,
      handleStripeResponse,
      handleNuveiResponse,
      handleBraintreeResponse,
      handleAdyenResponse,
      handlePaypalResponse,
    ]
  );

  return {
    handlePaymentResults,
  };
};
